'use client';

import React, { memo, PropsWithChildren, useMemo, useRef } from 'react';
import { AnimatePresence } from 'motion/react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components/ui/sheet';

import { YtVideoCardAnalysisClose } from './yt-video-card-analysis-close';
import { useYtVideoCardAnalysis } from './yt-video-card-analysis-intent-context';

interface YtVideoCardAnalysisSidebarProps extends PropsWithChildren {
  open: boolean;
  setOpen: (open: boolean) => void;
  videoId: string;
}
const PureYtVideoCardAnalysisSidebar: React.FC<
  YtVideoCardAnalysisSidebarProps
> = ({ children, open, setOpen, videoId }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { intent } = useYtVideoCardAnalysis();

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams();
    params.set('embedded', 'true');

    if (intent?.openSection) params.set('section', intent.openSection);
    if (intent?.claimsTab) params.set('tab', intent.claimsTab);
    if (intent?.openComments) params.set('openComments', 'true');

    return `/videos/youtube/sidepanel/${videoId}?${params.toString()}`;
  }, [videoId, intent]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        side="right"
        overlayClassName="bg-black/30"
        className="size-full border-none bg-black p-0 text-sidebar-foreground min-[20rem]:w-cred-sm sm:max-w-full min-[81.25rem]:w-cred-md min-[112.5rem]:w-cred-lg min-[156.25rem]:w-cred-xl [&>button]:hidden"
      >
        <AnimatePresence mode="wait">
          {open && <YtVideoCardAnalysisClose setIsVisible={setOpen} />}
        </AnimatePresence>
        <SheetHeader className="sr-only">
          <SheetTitle>Yt Video Analysis</SheetTitle>
          <SheetDescription>
            Displays the yt video analysis sidebar.
          </SheetDescription>
        </SheetHeader>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          className="size-full border-none"
          title="Yt Video Analysis"
        />
      </SheetContent>
    </Sheet>
  );
};
export const YtVideoCardAnalysisSidebar = memo(PureYtVideoCardAnalysisSidebar);
